import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useModulos } from "@/hooks/use-modulos";
import {
  useCreditosSaldo,
  useCreditosTransacoes,
  useEmpresas,
} from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useLocation } from "wouter";
import { Plus, Wallet, TrendingUp, Eye, FlaskConical } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { VEICULO_TIPO_LABELS } from "@/lib/constants";
import { creditosLivres } from "@shared/credito-saldo";

const TIPO_VEICULO_EMOJIS: Record<string, { emoji: string }> = {
  auto_leve: { emoji: "🚗" },
  moto: { emoji: "🏍️" },
  caminhao: { emoji: "🚛" },
  onibus: { emoji: "🚌" },
};

const TIPOS_VEICULO_ORDEM = ["auto_leve", "moto", "caminhao", "onibus"];

const TRANSACAO_TIPOS: Record<string, string> = {
  compra: "Compra de Crédito",
  reserva: "Reserva (Laudo Enviado)",
  debito: "Débito (Laudo Finalizado)",
  liberacao: "Liberação (1 Dia Útil)",
  estorno_credito: "Estorno (Crédito)",
  estorno_dinheiro: "Estorno (Dinheiro)",
};

export default function Creditos() {
  const { profile } = useAuth();
  const { modulos, isLoading: modulosLoading } = useModulos();
  const [, setLocation] = useLocation();

  const empresaId = profile?.empresaId;
  const empresaTipo = profile?.empresa?.tipo ?? "";
  const podeComprar = empresaTipo === "PARCEIRO_DE_NEGOCIOS";

  // Hooks sempre antes de qualquer return (Regra dos Hooks). Com o módulo desligado, ou ainda
  // carregando, o id fica indefinido e as consultas não disparam.
  const empresaIdCreditos = modulos.creditosPrePagos ? empresaId : undefined;
  const { data: saldos, isLoading: saldosLoading } =
    useCreditosSaldo(empresaIdCreditos);
  const { data: transacoes, isLoading: transacoesLoading } =
    useCreditosTransacoes(empresaIdCreditos);

  if (!modulosLoading && !modulos.creditosPrePagos) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Wallet className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Módulo não disponível</h2>
          <p className="text-muted-foreground max-w-sm">
            O módulo de Créditos Pré-Pagos não está ativo para sua vistoriadora.
          </p>
        </div>
      </Shell>
    );
  }

  if (!empresaId) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Empresa não identificada.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Créditos</h1>
            <p className="text-muted-foreground mt-2">
              Gerencie seus créditos para solicitar laudos
            </p>
          </div>
          {podeComprar && (
            <Button
              onClick={() => setLocation("/creditos/comprar")}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Comprar Créditos
            </Button>
          )}
        </div>

        {/* Saldos por tipo de veículo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {saldosLoading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="border-none shadow-md">
                  <CardContent className="pt-6">
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            TIPOS_VEICULO_ORDEM.map((tipo) => {
              const saldo = saldos?.find((s) => s.tipo_veiculo === tipo);
              const info = {
                emoji: TIPO_VEICULO_EMOJIS[tipo].emoji,
                label: VEICULO_TIPO_LABELS[tipo],
              };

              return (
                <Card key={tipo} className="border-none shadow-md">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-3xl">{info.emoji}</span>
                        <Wallet className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {info.label}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                          {creditosLivres(saldo)}
                        </p>
                        <p className="text-xs text-muted-foreground">Créditos livres</p>
                        <p className="text-xs text-amber-600 mt-1">
                          {saldo?.reservado ?? 0} reservado(s)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Histórico de transações */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Histórico de Transações
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transacoesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : transacoes && transacoes.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo Veículo</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transacoes.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell className="text-sm">
                          {VEICULO_TIPO_LABELS[tx.tipo_veiculo]}
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(tx.criado_em), "dd MMM yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell className="text-sm">
                          {TRANSACAO_TIPOS[tx.tipo] || tx.tipo}
                        </TableCell>
                        <TableCell className="font-medium">
                          {tx.tipo === "estorno_credito" ||
                          tx.tipo === "estorno_dinheiro"
                            ? `-${tx.quantidade}`
                            : `+${tx.quantidade}`}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline">
                              {tx.tipo === "compra" ? "Processado" : "Concluído"}
                            </Badge>
                            {(tx as any).descricao?.includes("simulado_teste") && (
                              <Badge className="bg-purple-100 text-purple-700 gap-1">
                                <FlaskConical className="w-3 h-3" />
                                Simulado
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {tx.tipo === "compra" && tx.compra_id ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setLocation(
                                  `/creditos/comprovante/${tx.compra_id}`,
                                )
                              }
                              className="gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              <span className="hidden sm:inline">Ver</span>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              -
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Nenhuma transação registrada
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
